// Pure, framework-free index + match logic for the navbar's global search
// (command-palette style — GlobalSearch.jsx). Extracted the same way
// sidebarNav.mjs and pageTitleLogic.mjs are: no React/DOM imports, so it's
// covered by `node --test` (frontend's `test:logic`, the suite CI actually
// runs) rather than only eyeballed in the browser.
//
// v1 index = the app's own navigable destinations — every sidebar nav leaf
// (sidebarNav.mjs) plus every Settings workspace page (pages/settings/
// sections.js), each permission-filtered with the exact same hasAnyPermission
// rule the sidebar/header account menu already use, so search can never
// surface a page the user would then get a 403 on.
//
// v2 (this file) adds a real ENTITY index alongside the static pages:
// districts/sectors, models/notebooks, active alerts, and datasets/tables.
// Same rule applies — every entity type carries a `requireAny` sourced from
// the REAL backend route gate for the endpoint it comes from (not guessed),
// so an entity result can never lead to a 403 either:
//   - alerts   -> services/model-intelligence/routes/alerts.js
//                 GET '/' ... requirePermission('view_predictions')
//   - models   -> services/model-intelligence/routes/notebooks.js
//                 GET '/' ... authenticate only, no extra permission
//   - datasets -> services/dynamic-schema/routes/tables.js
//                 GET '/' ... authenticate only (auditLog), no extra permission
// Districts/sectors aren't behind their own endpoint (they're the static
// NISR adm2/adm3 catalogue, data/rwanda.js) but exist here purely to jump
// into the alerts view scoped to a district/sector, so they're gated the
// same as alerts.
//
// The entity DATA itself (notebooks/alerts/tables rows) is fetched by
// hooks/useGlobalSearch.js's useGlobalSearchEntities() — debounced, cached,
// and permission-gated at the fetch level too (no point calling an endpoint
// whose gate the current user will 403 on). This module only ever receives
// already-fetched arrays and turns them into indexed, permission-filtered,
// rankable entries — same contract as the page index below.

import { hasAnyPermission } from './permissions.js'
import { NAV_ITEMS } from '../components/sidebarNav.mjs'
import { SETTINGS_SECTIONS, SETTINGS_SECTION_ORDER } from '../pages/settings/sections.js'
import { rwandaDistricts } from '../data/rwanda.js'

// The sidebar collapses all of Settings into a single childless leaf
// (/app/settings) — its own in-page nav (SettingsLayout) is what actually
// discriminates between pages. For search we want the opposite: skip that
// single collapsed leaf and index each real Settings page individually
// (settingsDestinations, below), each gated on its OWN requireAny — finer
// grained than the sidebar leaf's unioned requireAny, so a user only ever
// sees settings pages they can actually open.
const NAV_HREF_EXCLUDE = new Set(['/app/settings'])

// Always-reachable account routes that live outside the sidebar nav
// entirely (Header's own account menu) but are still real destinations
// someone would reasonably search for.
const EXTRA_DESTINATIONS = [
  {
    id: 'account:profile',
    type: 'page',
    label: 'My account',
    path: '/app/account',
    icon: 'User',
    group: 'Account',
    keywords: 'account settings profile security password preferences avatar',
    requireAny: undefined,
    parentRequireAny: undefined,
  },
]

function navDestinations() {
  const out = []
  for (const item of NAV_ITEMS) {
    if (item.children && item.children.length) {
      for (const child of item.children) {
        out.push({
          id: `nav:${child.href}`,
          type: 'page',
          label: child.name,
          path: child.href,
          icon: child.icon,
          group: item.name,
          keywords: [item.name, item.description, child.description].filter(Boolean).join(' '),
          // A child's own gate AND its parent group's gate both have to
          // pass — mirrors buildSidebarSections' two-step filter exactly
          // (see sidebarNav.mjs), so search visibility can never diverge
          // from what the sidebar itself would show.
          requireAny: child.requireAny,
          parentRequireAny: item.requireAny,
        })
      }
    } else if (item.href && !NAV_HREF_EXCLUDE.has(item.href)) {
      out.push({
        id: `nav:${item.href}`,
        type: 'page',
        label: item.name,
        path: item.href,
        icon: item.icon,
        group: 'Navigate',
        keywords: item.description || '',
        requireAny: item.requireAny,
        parentRequireAny: undefined,
      })
    }
  }
  return out
}

function settingsDestinations() {
  const out = []
  for (const key of SETTINGS_SECTION_ORDER) {
    const section = SETTINGS_SECTIONS[key]
    if (!section) continue
    for (const page of section.pages) {
      out.push({
        id: `settings:${page.path}`,
        type: 'page',
        label: page.label,
        path: `/app/settings/${page.path}`,
        icon: page.icon,
        group: `Settings / ${section.label}`,
        keywords: `settings ${section.label} ${page.label}`,
        requireAny: page.requireAny,
        parentRequireAny: undefined,
      })
    }
  }
  return out
}

// Full destination catalogue, pre permission-filter. Exported so tests can
// assert against the raw shape without needing a fake user.
export function allDestinations() {
  return [...navDestinations(), ...settingsDestinations(), ...EXTRA_DESTINATIONS]
}

// Permission-filtered index for a given user — the list GlobalSearch.jsx
// actually matches against.
export function buildSearchIndex(user) {
  return allDestinations().filter(
    (d) => hasAnyPermission(user, d.requireAny) && hasAnyPermission(user, d.parentRequireAny)
  )
}

// Case-insensitive substring match across label / group / keywords, ranked
// so the most relevant hit sorts first:
//   0 exact label match
//   1 label starts with the query
//   2 label contains the query
//   3 group or keywords contain the query
// Ties keep the index's own (nav) order — Array.prototype.sort is stable
// per spec in every JS engine this app targets, so equal-score entries
// never get reshuffled between renders.
function matchScore(entry, needle) {
  const label = (entry.label || '').toLowerCase()
  if (label === needle) return 0
  if (label.startsWith(needle)) return 1
  if (label.includes(needle)) return 2
  const hay = `${entry.group || ''} ${entry.keywords || ''}`.toLowerCase()
  if (hay.includes(needle)) return 3
  return -1
}

export function searchDestinations(query, index, limit = 8) {
  const needle = String(query || '').trim().toLowerCase()
  if (!needle || !Array.isArray(index)) return []

  const scored = []
  for (const entry of index) {
    const s = matchScore(entry, needle)
    if (s >= 0) scored.push({ entry, s })
  }
  scored.sort((a, b) => a.s - b.s)
  return scored.slice(0, limit).map((x) => x.entry)
}

// =====================================================================
// Entity index — districts/sectors, models/notebooks, active alerts,
// datasets/tables. Each builder below takes already-fetched raw rows (or,
// for districts/sectors, the static data/rwanda.js catalogue by default)
// and turns them into entries shaped exactly like the page destinations
// above (id/type/label/path/icon/group/keywords/requireAny), so they can
// be scored by the same matchScore() and merged/ranked/grouped alongside
// pages with zero special-casing.
// =====================================================================

// See the file-header comment for where each of these traces to a real
// backend requirePermission() (or its absence) — never guessed.
export const ENTITY_REQUIRE_ANY = {
  district: ['view_predictions'],
  sector: ['view_predictions'],
  alert: ['view_predictions'],
  model: undefined,
  dataset: undefined,
}

// Districts + sectors — Rwanda's 30 districts / 416 sectors (data/rwanda.js,
// the same canonical NISR catalogue the dashboard's choropleth and rising-
// sectors table use). Both jump into the Alerts view scoped to that
// district/sector via query params (`?district=`, `&sector=`) — AlertsPage
// doesn't read those params yet (it's local-state filtering, not URL-driven),
// so today this lands on the unfiltered Alerts list; the params are there so
// wiring that read is a follow-up, not a second round of search plumbing.
export function districtEntities(districts = rwandaDistricts) {
  const out = []
  for (const d of districts || []) {
    out.push({
      id: `district:${d.id}`,
      type: 'district',
      label: d.name,
      path: `/app/alerts?district=${encodeURIComponent(d.name)}`,
      icon: 'MapPin',
      group: 'Districts',
      keywords: `district ${d.name} ${d.province || ''}`,
      requireAny: ENTITY_REQUIRE_ANY.district,
    })
    for (const s of d.sectors || []) {
      out.push({
        id: `sector:${s.id}`,
        type: 'sector',
        label: `${s.name} · ${d.name}`,
        path: `/app/alerts?district=${encodeURIComponent(d.name)}&sector=${encodeURIComponent(s.name)}`,
        icon: 'MapPin',
        group: 'Sectors',
        keywords: `sector ${s.name} ${d.name} ${d.province || ''}`,
        requireAny: ENTITY_REQUIRE_ANY.sector,
      })
    }
  }
  return out
}

// Models/notebooks — notebooksAPI.list() rows (models_schema.notebooks:
// notebook_id, disease, version, name, is_active, ...). No per-notebook
// route exists yet (RegistryPage picks a disease from local state, not a
// URL) — every result lands on /app/models, the reachable page for this
// feature.
export function modelEntities(notebooks = []) {
  return (notebooks || [])
    .filter((n) => n && n.notebook_id)
    .map((n) => ({
      id: `model:${n.notebook_id}`,
      type: 'model',
      label: `${n.disease || 'Model'} — v${n.version ?? '?'}${n.is_active ? ' (active)' : ''}`,
      path: '/app/models',
      icon: 'Brain',
      group: 'Models',
      keywords: `model notebook ${n.disease || ''} v${n.version || ''} ${n.name || ''} ${n.is_active ? 'active' : 'inactive'}`,
      requireAny: ENTITY_REQUIRE_ANY.model,
    }))
}

// Active alerts — alertsAPI.list() rows (alerts_schema.sector_anomalies:
// anomaly_id, district_code, location_code, metric_type, severity, ...).
// location_code carries a leading "NNN_" NISR id (see alertsController.js'
// own note on the same convention) — stripped here purely for a readable
// label, the full location_code stays in keywords so it's still searchable.
export function alertEntities(alerts = []) {
  return (alerts || [])
    .filter((a) => a && a.anomaly_id)
    .map((a) => {
      const shortLocation = String(a.location_code || '').replace(/^[^_]+_/, '')
      const place = shortLocation || a.district_code || 'Alert'
      return {
        id: `alert:${a.anomaly_id}`,
        type: 'alert',
        label: `${place} — ${a.metric_type || 'anomaly'}`,
        path: `/app/alerts?anomaly=${encodeURIComponent(a.anomaly_id)}`,
        icon: 'BellRing',
        group: 'Active alerts',
        keywords: `alert ${a.district_code || ''} ${a.location_code || ''} ${shortLocation} ${a.metric_type || ''} ${a.severity || ''} ${a.source || ''}`,
        requireAny: ENTITY_REQUIRE_ANY.alert,
      }
    })
}

// Datasets/tables — schemaAPI.getTables() rows (dynamic-schema's
// information_schema listing: name, schema, columns, description). Every
// result lands on the Tables page (/app/schema/tables), the only place
// these are browsable today.
export function datasetEntities(tables = []) {
  return (tables || [])
    .filter((t) => t && t.name)
    .map((t) => ({
      id: `dataset:${t.name}`,
      type: 'dataset',
      label: t.name,
      path: '/app/schema/tables',
      icon: 'Table',
      group: 'Datasets',
      keywords: `table dataset ${t.name} ${t.description || ''}`,
      requireAny: ENTITY_REQUIRE_ANY.dataset,
    }))
}

// Permission-filtered entity index for a given user, built from
// already-fetched raw rows — same contract as buildSearchIndex() above.
// Every entity type independently defaults to empty so callers only need
// to pass what they've actually fetched (e.g. before the alerts fetch
// resolves, `alerts` is simply omitted and no alert entries appear yet).
export function buildEntityIndex(user, { districts, notebooks, alerts, tables } = {}) {
  const all = [
    ...districtEntities(districts),
    ...modelEntities(notebooks),
    ...alertEntities(alerts),
    ...datasetEntities(tables),
  ]
  return all.filter((e) => hasAnyPermission(user, e.requireAny))
}

// Merge + rank pages and entities in one pass, using the exact same
// matchScore() as searchDestinations() so a district/model/alert/dataset
// competes fairly against a page for the same query (an exact label match
// on a district always outranks a page that only contains the query in its
// keywords, etc). No result cap here — grouping (below) caps per type, so
// one huge type (416 sectors) can never crowd out the others before
// grouping gets a chance to balance them.
export function searchCombined(query, { pageIndex = [], entityIndex = [] } = {}) {
  const needle = String(query || '').trim().toLowerCase()
  if (!needle) return []

  const scored = []
  for (const entry of [...(pageIndex || []), ...(entityIndex || [])]) {
    const s = matchScore(entry, needle)
    if (s >= 0) scored.push({ entry, s })
  }
  scored.sort((a, b) => a.s - b.s)
  return scored.map((x) => x.entry)
}

// Display order + labels for grouped results. Pages first (v1's own
// behaviour, least surprising), then the most time-sensitive entity type
// (alerts), then models/datasets, then the geo catalogue.
const GROUP_ORDER = ['page', 'alert', 'model', 'dataset', 'district', 'sector']
const GROUP_LABELS = {
  page: 'Pages',
  alert: 'Active alerts',
  model: 'Models',
  dataset: 'Datasets',
  district: 'Districts',
  sector: 'Sectors',
}

// Buckets an already-ranked flat result list (searchCombined's output) by
// `type`, capping each bucket at `limitPerGroup` — independently of where
// in the global ranking a type's matches happen to fall, so e.g. 30
// district matches for "a" don't push every alert/model/dataset match off
// the end of a single flat top-N. Within each bucket, the original
// (score-sorted) relative order is preserved.
export function groupSearchResults(results, { limitPerGroup = 6 } = {}) {
  if (!Array.isArray(results) || results.length === 0) return []

  const byType = new Map()
  for (const entry of results) {
    const type = entry.type || 'page'
    if (!byType.has(type)) byType.set(type, [])
    const bucket = byType.get(type)
    if (bucket.length < limitPerGroup) bucket.push(entry)
  }

  const groups = []
  for (const type of GROUP_ORDER) {
    const items = byType.get(type)
    if (items && items.length) {
      groups.push({ type, label: GROUP_LABELS[type] || type, items })
      byType.delete(type)
    }
  }
  // Any type not in GROUP_ORDER (future-proofing a new entity type) is
  // still shown, just appended after the known groups instead of dropped.
  for (const [type, items] of byType) {
    if (items.length) groups.push({ type, label: GROUP_LABELS[type] || type, items })
  }
  return groups
}
